"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { GlassCard } from "./glass-card";

const SAMPLE_MESSAGES = [
  { channel: "WhatsApp", text: "Mon colis n'arrive pas, commande #2847. C'est urgent !", tags: ["Livraison", "Urgent"], priority: "high" },
  { channel: "Email", text: "Bonjour, je souhaite un remboursement pour ma commande du 12/03.", tags: ["Remboursement"], priority: "normal" },
  { channel: "Chat", text: "Super expérience, livraison rapide et produit conforme. Merci !", tags: ["Satisfaction"], priority: "low" },
];

function TypingLine({ text, onComplete }: { text: string; onComplete: () => void }) {
  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    if (displayed >= text.length) {
      onComplete();
      return;
    }
    const t = setTimeout(() => setDisplayed((d) => d + 1), 40);
    return () => clearTimeout(t);
  }, [text, displayed, onComplete]);
  return <span>{text.slice(0, displayed)}</span>;
}

export function IaEngine() {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<"typing" | "tags">("typing");
  const current = SAMPLE_MESSAGES[index % SAMPLE_MESSAGES.length];

  useEffect(() => {
    if (phase !== "tags") return;
    const t = setTimeout(() => {
      setPhase("typing");
      setIndex((i) => i + 1);
    }, 2500);
    return () => clearTimeout(t);
  }, [phase]);

  const handleTypingComplete = () => {
    setPhase("tags");
  };

  return (
    <section className="relative px-4 py-20 md:py-28">
      <div className="mx-auto max-w-4xl">
        <motion.h2
          className="text-center text-3xl font-bold tracking-tight text-white sm:text-4xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          L&apos;IA au cœur de chaque ticket
        </motion.h2>
        <motion.p
          className="mx-auto mt-4 max-w-xl text-center text-slate-400"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Chaque nouveau message est analysé en temps réel : tags, priorité et sentiment.
        </motion.p>

        <motion.div
          className="mt-12 flex justify-center"
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <GlassCard className="w-full max-w-lg overflow-hidden p-0" padding="none">
            <div className="border-b border-white/10 bg-white/5 px-5 py-3">
              <span className="text-xs font-medium uppercase tracking-wider text-cyan-400">
                {current.channel}
              </span>
            </div>
            <div className="min-h-[140px] p-5">
              <p className="min-h-[1.5em] font-mono text-sm text-slate-300">
                {phase === "typing" ? (
                  <TypingLine text={current.text} onComplete={handleTypingComplete} />
                ) : (
                  current.text
                )}
              </p>
              <AnimatePresence mode="wait">
                {phase === "tags" && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 flex flex-wrap items-center gap-2"
                  >
                    <Sparkles className="h-4 w-4 text-amber-400" aria-hidden />
                    {current.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-medium text-cyan-300"
                      >
                        {tag}
                      </span>
                    ))}
                    <span className="rounded-lg border border-white/20 bg-white/5 px-3 py-1 text-xs text-slate-400">
                      {current.priority}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </section>
  );
}
